import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ApiService } from '../../service/api.service';
import { CategoryFilter } from '../../service/api.dto';
import { Product } from '../../components/products/products.component';

export interface RecommendationItem {
  type: 'upsell' | 'cross';
  todoId: string;
  todoName: string;
  productId: string;
  baseProduct: Product;
  product?: Product;
  message?: string;
  loaded: boolean;
}

export interface CurrentRecommendation {
  type: 'upsell' | 'cross';
  product: Product;
  message: string;
  todoId: string;
  todoName: string;
}

export const RECOMMENDATIONS_SERVER_ERROR_MESSAGE =
  'Lo sentimos, estamos teniendo dificultades para resolver tu petición. Intentalo de nuevo en unos momentos.';

@Injectable({
  providedIn: 'root'
})
export class RecommendationsFlowService {
  
  private upsellingQueue: Array<{ todoId: string; todoName: string; productId: string; product: Product }> = [];
  private crossSellingQueue: Array<{ todoId: string; todoName: string; productId: string; product: Product; categories?: CategoryFilter[] }> = [];
  private upsellingResults: Map<string, { loaded: boolean; product?: any; message?: string }> = new Map();
  private crossSellingResults: Map<string, { loaded: boolean; product?: any; message?: string }> = new Map();
  private crossSellingBatchLoaded: boolean = false;
  private crossSellingBatchProducts: Product[] = [];
  private crossSellingBatchMessage: string = '';
  private currentUpsellingIndex: number = 0;
  private currentCrossSellingIndex: number = 0;
  private totalExpectedRecommendations: number = 0;
  private totalLoadedRecommendations: number = 0;
  private recommendationFlowStarted: boolean = false;
  private isShowingRecommendation: boolean = false;

  private currentRecommendationSubject = new BehaviorSubject<CurrentRecommendation | null>(null);
  public currentRecommendation$ = this.currentRecommendationSubject.asObservable();

  private flowFinishedSubject = new BehaviorSubject<boolean>(false);
  public flowFinished$ = this.flowFinishedSubject.asObservable();

  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  public isLoading$ = this.isLoadingSubject.asObservable();

  private errorMessageSubject = new BehaviorSubject<string | null>(null);
  public errorMessage$ = this.errorMessageSubject.asObservable();

  constructor(private apiService: ApiService) {}

  private log(message: string, data?: any) {
    const prefix = '[RecommendationsFlow]';
    if (data !== undefined) {
      // console.log(`${prefix} ${message}`, data);
    } else {
      // console.log(`${prefix} ${message}`);
    }
  }

  /**
   * Determina el mensaje de upselling según upselling_text_type
   * Si es "custom", usa upselling_text del POS
   * Si es "ai_generated", usa el mensaje del backend
   */
  private getUpsellingMessage(backendMessage?: string, fallback?: string): string {
    const posDetails = this.apiService.details;
    if (!posDetails) {
      return backendMessage || fallback || '';
    }

    // Si upselling_text_type es "custom", usar upselling_text
    if (posDetails.upselling_text_type === 'custom') {
      return posDetails.upselling_text || fallback || '';
    }

    // Si es "ai_generated" o cualquier otro valor, usar el mensaje del backend
    return backendMessage || fallback || '';
  }

  /**
   * Determina el mensaje de cross-selling según cross_selling_text_type
   * Si es "custom", usa cross_selling_text del POS
   * Si es "ai_generated", usa el mensaje del backend
   */
  private getCrossSellingMessage(backendMessage?: string, fallback?: string): string {
    const posDetails = this.apiService.details;
    if (!posDetails) {
      return backendMessage || fallback || '';
    }

    // Si cross_selling_text_type es "custom", usar cross_selling_text
    if (posDetails.cross_selling_text_type === 'custom') {
      return posDetails.cross_selling_text || fallback || '';
    }

    // Si es "ai_generated" o cualquier otro valor, usar el mensaje del backend
    return backendMessage || fallback || '';
  }

  resetState() {
    this.upsellingQueue = [];
    this.crossSellingQueue = [];
    this.upsellingResults.clear();
    this.crossSellingResults.clear();
    this.currentUpsellingIndex = 0;
    this.currentCrossSellingIndex = 0;
    this.totalExpectedRecommendations = 0;
    this.totalLoadedRecommendations = 0;
    this.recommendationFlowStarted = false;
    this.isShowingRecommendation = false;
    this.crossSellingBatchLoaded = false;
    this.crossSellingBatchProducts = [];
    this.crossSellingBatchMessage = '';
    this.currentRecommendationSubject.next(null);
    this.flowFinishedSubject.next(false);
    this.isLoadingSubject.next(false);
    this.errorMessageSubject.next(null);
  }

  queueRecommendations(todoId: string, todoName: string, product: Product, categories?: CategoryFilter[]) {
    if (!this.apiService.details) return;

    // Prevenir llamadas duplicadas para el mismo todo
    if (this.upsellingResults.has(todoId) || this.crossSellingResults.has(todoId)) {
      this.log('Recomendaciones duplicadas detectadas, se ignora', { todoId, todoName });
      return;
    }

    this.log('Solicitando upselling/cross-selling', { todoId, todoName, productId: product.productID });

    // Agregar a las colas e inicializar resultados solo cuando aplique
    if (this.apiService.details.upselling_enabled) {
      this.upsellingQueue.push({ todoId, todoName, productId: product.productID, product });
      this.upsellingResults.set(todoId, { loaded: false });
    }
    // Para cross-sell, solo guardamos la información, NO hacemos la llamada todavía
    if (this.apiService.details.cross_selling_enabled) {
      this.crossSellingQueue.push({ todoId, todoName, productId: product.productID, product, categories });
    }

    // Calcular cuántas recomendaciones esperamos (solo upselling ahora, cross-sell se hará después)
    let expectedForThisItem = 0;
    if (this.apiService.details.upselling_enabled) expectedForThisItem++;
    // NO contamos cross-sell aquí porque se hará en batch después
    
    this.totalExpectedRecommendations += expectedForThisItem;
    this.log('Total de recomendaciones esperadas actualizado', this.totalExpectedRecommendations);

    // Solo hacer las llamadas si el upselling/cross-selling está habilitado
    if (this.apiService.details.upselling_enabled) {
      this.apiService.getUpsellingProducts(
        this.apiService.details.catalog.id,
        this.apiService.details.id,
        product.productID
      ).subscribe({
        next: (data) => {
          this.log('Upselling cargado', { todoId, todoName, data });
          if (data.success && data.upsellingProducts?.producto) {
            this.upsellingResults.set(todoId, {
              loaded: true,
              product: data.upsellingProducts.producto,
              message: data.upsellingProducts.explicacionDeRecomendacion
            });
          } else {
            this.upsellingResults.set(todoId, { loaded: true });
          }
          this.totalLoadedRecommendations++;
          this.log('Contador de recomendaciones actualizado', { loaded: this.totalLoadedRecommendations, expected: this.totalExpectedRecommendations });
          this.onRecommendationLoaded();
        },
        error: (err) => {
          this.log('Error al cargar upselling', { todoId, todoName, error: err });
          this.upsellingResults.set(todoId, { loaded: true });
          this.totalLoadedRecommendations++;
          this.log('Contador de recomendaciones actualizado', { loaded: this.totalLoadedRecommendations, expected: this.totalExpectedRecommendations });
          this.notifyServerErrorIfNeeded(err);
          this.onRecommendationLoaded();
        }
      });
    }

    // NO hacemos la llamada de cross-sell aquí, se hará en batch cuando termine el flujo de upsell
  }

  private onRecommendationLoaded() {
    this.log('onRecommendationLoaded', { loaded: this.totalLoadedRecommendations, expected: this.totalExpectedRecommendations });
    
    // Si el flujo ya comenzó y todas las recomendaciones cargaron, intentar mostrar
    if (this.recommendationFlowStarted && 
        this.totalLoadedRecommendations >= this.totalExpectedRecommendations &&
        !this.isShowingRecommendation) {
      this.log('Todas las recomendaciones cargaron durante el flujo, mostrando siguiente');
      setTimeout(() => {
        this.showNextAvailableRecommendation();
      }, 300);
    }
  }

  startFlow() {
    this.log('Iniciando flujo de recomendaciones', { 
      expected: this.totalExpectedRecommendations,
      loaded: this.totalLoadedRecommendations,
      upsellingQueueLength: this.upsellingQueue.length,
      crossSellingQueueLength: this.crossSellingQueue.length
    });
    
    // Si no hay recomendaciones de upsell esperadas, verificar si al menos hay cross-sell
    if (this.totalExpectedRecommendations === 0) {
      const hasCrossSelling =
        this.apiService.details?.cross_selling_enabled && this.crossSellingQueue.length > 0;

      // Marcar que el flujo comenzó para mantener la consistencia del estado
      this.recommendationFlowStarted = true;

      if (hasCrossSelling) {
        // Caso especial: solo hay cross-sell (sin upsell).
        // En este caso igual debemos disparar la llamada batch de cross-sell
        // para que Step4 pueda mostrar las recomendaciones cuando carguen.
        this.log('Solo hay cross-selling habilitado, iniciando búsqueda de cross-sell en segundo plano');
        const crossSellingData = this.getCrossSellingQueueData();
        this.loadCrossSellingBatch(crossSellingData);
        // No llamamos a finishFlow aquí, ya que el flujo integrado maneja el avance de pasos
      } else {
        this.log('No hay recomendaciones esperadas ni cross-sell, finalizando flujo inmediatamente');
        this.finishFlow();
      }
      return;
    }
    
    this.isLoadingSubject.next(true);
    
    // Marcar que el flujo de recomendaciones comenzó
    this.recommendationFlowStarted = true;
    
    // Iniciar la búsqueda de cross-sell en segundo plano si está habilitado
    // Esto se hace ANTES de mostrar los upsells para no hacer esperar al usuario
    if (this.apiService.details?.cross_selling_enabled && this.crossSellingQueue.length > 0) {
      this.log('Iniciando búsqueda de cross-sell en segundo plano');
      const crossSellingData = this.getCrossSellingQueueData();
      this.loadCrossSellingBatch(crossSellingData);
    }
    
    // Verificar si todas las recomendaciones ya cargaron
    if (this.totalLoadedRecommendations >= this.totalExpectedRecommendations) {
      this.log('Todas las recomendaciones ya cargaron antes de iniciar la espera');
      // Si ya todas cargaron, mostrar inmediatamente
      setTimeout(() => {
        this.showNextAvailableRecommendation();
      }, 500);
    } else {
      this.log('Esperando carga de recomendaciones pendientes', {
        loaded: this.totalLoadedRecommendations,
        expected: this.totalExpectedRecommendations
      });
      this.waitForAllRecommendations();
    }
  }

  private waitForAllRecommendations() {
    // Esperar máximo 10 segundos para que carguen todas las recomendaciones
    const checkInterval = setInterval(() => {
      this.log('Verificando estado de carga de recomendaciones', { loaded: this.totalLoadedRecommendations, expected: this.totalExpectedRecommendations });
      
      if (this.totalLoadedRecommendations >= this.totalExpectedRecommendations) {
        this.log('Todas las recomendaciones terminaron de cargar');
        clearInterval(checkInterval);
        
        if (!this.isShowingRecommendation) {
          this.showNextAvailableRecommendation();
        }
      }
      
      // Verificar también si no hay recomendaciones esperadas (caso cuando no hay productos)
      if (this.totalExpectedRecommendations === 0) {
        this.log('No hay recomendaciones esperadas, finalizando flujo');
        clearInterval(checkInterval);
        if (!this.isShowingRecommendation) {
          this.finishFlow();
        }
      }
    }, 300);

    // Timeout de seguridad - reducir a 5 segundos para no esperar tanto
    setTimeout(() => {
      clearInterval(checkInterval);
      this.log('Timeout esperando recomendaciones', { loaded: this.totalLoadedRecommendations, expected: this.totalExpectedRecommendations });
      // Mostrar las que hayan cargado (solo si no estamos mostrando algo)
      if (!this.isShowingRecommendation) {
        this.showNextAvailableRecommendation();
      }
    }, 5000);
  }

  private showNextAvailableRecommendation() {
    this.log('showNextAvailableRecommendation invocado', {
      isShowingRecommendation: this.isShowingRecommendation,
      currentUpsellingIndex: this.currentUpsellingIndex,
      upsellingQueueLength: this.upsellingQueue.length,
      currentCrossSellingIndex: this.currentCrossSellingIndex,
      crossSellingQueueLength: this.crossSellingQueue.length
    });
    
    // Si ya estamos mostrando algo, no hacer nada
    if (this.isShowingRecommendation) {
      this.log('showNextAvailableRecommendation cancelado porque ya se está mostrando una recomendación');
      return;
    }

    // Si no hay recomendaciones esperadas, finalizar inmediatamente
    if (this.totalExpectedRecommendations === 0 || 
        (this.upsellingQueue.length === 0 && this.crossSellingQueue.length === 0)) {
      this.log('No hay recomendaciones en cola, finalizando flujo');
      this.finishFlow();
      return;
    }

    // Verificar si todas las recomendaciones están procesadas
    const allUpsellingsProcessed = this.currentUpsellingIndex >= this.upsellingQueue.length;
    const allCrossSellingsProcessed = this.currentCrossSellingIndex >= this.crossSellingQueue.length;
    
    if (allUpsellingsProcessed && allCrossSellingsProcessed) {
      this.log('Todas las recomendaciones fueron procesadas, finalizando flujo');
      this.finishFlow();
      return;
    }

    // Primero intentar mostrar upsellings pendientes
    if (this.currentUpsellingIndex < this.upsellingQueue.length) {
      const current = this.upsellingQueue[this.currentUpsellingIndex];
      const result = this.upsellingResults.get(current.todoId);

      if (!result) {
        this.log('Upselling sin resultado, se avanza al siguiente', { index: this.currentUpsellingIndex, todoId: current.todoId });
        this.currentUpsellingIndex++;
        if (this.currentUpsellingIndex <= this.upsellingQueue.length) {
          setTimeout(() => this.showNextAvailableRecommendation(), 100);
        } else {
          this.finishFlow();
        }
        return;
      }

      if (!result.loaded) {
        this.log('Upselling aún no cargado, reintentando', { todoId: current.todoId });
        setTimeout(() => {
          this.showNextAvailableRecommendation();
        }, 500);
        return;
      }

      if (result.product) {
        this.log('Mostrando upselling', { todoId: current.todoId, productId: result.product.id });
        this.isShowingRecommendation = true;
        this.isLoadingSubject.next(false);
        
        const recommendation: CurrentRecommendation = {
          type: 'upsell',
          product: {
            load: false,
            productID: result.product.id,
            name: result.product.name,
            price: result.product.price,
            image: result.product.photourl,
            description: result.product.search_aggregate
          },
          message: this.getUpsellingMessage(result.message, `¿Querés mejorar tu ${current.todoName}?`),
          todoId: current.todoId,
          todoName: current.todoName
        };

        this.currentRecommendationSubject.next(recommendation);
        return;
      } else {
        this.log('Upselling sin producto disponible, se avanza', { todoId: current.todoId });
        this.currentUpsellingIndex++;
        setTimeout(() => this.showNextAvailableRecommendation(), 100);
        return;
      }
    }

    // Si terminamos con upsellings, NO intentar cross-sellings individuales
    // porque ahora se hacen en batch después de completar el flujo de upsell
    // Simplemente finalizar el flujo

    // Si llegamos aquí, finalizar el flujo
    this.finishFlow();
  }

  acceptCurrent(product: Product): { type: 'upsell' | 'cross'; todoId: string } | null {
    const current = this.currentRecommendationSubject.value;
    if (!current) return null;

    this.log(`${current.type} aceptado`, { productId: product.productID });
    
    const result = { type: current.type, todoId: current.todoId };
    
    // Liberar banderas y pasar al siguiente
    this.isShowingRecommendation = false;
    this.currentRecommendationSubject.next(null);
    
    if (current.type === 'upsell') {
      this.currentUpsellingIndex++;
    } else {
      this.currentCrossSellingIndex++;
    }
    
    this.isLoadingSubject.next(true);
    
    setTimeout(() => {
      this.showNextAvailableRecommendation();
    }, 500);

    return result;
  }

  rejectCurrent() {
    const current = this.currentRecommendationSubject.value;
    if (!current) return;

    this.log(`${current.type} rechazado`);
    
    // Liberar banderas y pasar al siguiente
    this.isShowingRecommendation = false;
    this.currentRecommendationSubject.next(null);
    
    if (current.type === 'upsell') {
      this.currentUpsellingIndex++;
    } else {
      this.currentCrossSellingIndex++;
    }
    
    this.isLoadingSubject.next(true);
    
    setTimeout(() => {
      this.showNextAvailableRecommendation();
    }, 500);
  }

  private finishFlow() {
    this.log('Finalizando flujo de recomendaciones');
    this.isShowingRecommendation = false;
    this.currentRecommendationSubject.next(null);
    this.isLoadingSubject.next(false);
    this.flowFinishedSubject.next(true);
  }

  /**
   * Hace la llamada batch de cross-sell con todos los productos seleccionados
   * Este método debe ser llamado cuando termine el flujo de upsell
   */
  loadCrossSellingBatch(selectedProducts: Array<{ productId: string; categories?: CategoryFilter[] }>): void {
    if (!this.apiService.details || !this.apiService.details.cross_selling_enabled) {
      this.log('Cross-selling no habilitado, no se hace la llamada batch');
      this.crossSellingBatchLoaded = true;
      return;
    }

    if (selectedProducts.length === 0) {
      this.log('No hay productos seleccionados para cross-selling');
      this.crossSellingBatchLoaded = true;
      return;
    }

    this.log('Solicitando cross-selling batch', { productCount: selectedProducts.length });

    // Recopilar todos los product IDs
    const productIds = selectedProducts.map(p => p.productId);

    // Recopilar todas las categorías únicas
    const allCategories: CategoryFilter[] = [];
    const categoryMap = new Map<string, Set<string>>();

    selectedProducts.forEach(p => {
      if (p.categories) {
        p.categories.forEach(cat => {
          if (!categoryMap.has(cat.field)) {
            categoryMap.set(cat.field, new Set());
          }
          cat.values.forEach(val => categoryMap.get(cat.field)!.add(val));
        });
      }
    });

    categoryMap.forEach((values, field) => {
      allCategories.push({
        field,
        values: Array.from(values)
      });
    });

    this.apiService.getCrossSellingProductsBatch(
      this.apiService.details.catalog.id,
      this.apiService.details.id,
      productIds,
      allCategories.length > 0 ? allCategories : undefined
    ).subscribe({
      next: (data) => {
        this.log('Cross-selling batch cargado', { data });
        if (data.success && data.crosssellingProducts?.productos && data.crosssellingProducts.productos.length > 0) {
          this.crossSellingBatchProducts = data.crosssellingProducts.productos.map((p: any) => ({
            load: false,
            productID: p.id,
            name: p.name,
            price: p.price,
            image: p.photourl,
            description: p.search_aggregate
          }));
          this.crossSellingBatchMessage = data.crosssellingProducts.explicacionDeRecomendacion || '';
        } else {
          this.crossSellingBatchProducts = [];
          this.crossSellingBatchMessage = '';
        }
        this.crossSellingBatchLoaded = true;
      },
      error: (err) => {
        this.log('Error al cargar cross-selling batch', { error: err });
        this.crossSellingBatchProducts = [];
        this.crossSellingBatchMessage = '';
        this.crossSellingBatchLoaded = true;
        this.notifyServerErrorIfNeeded(err);
      }
    });
  }

  getCrossSellingBatchProducts(): Product[] {
    return this.crossSellingBatchProducts;
  }

  getCrossSellingBatchMessage(): string {
    return this.crossSellingBatchMessage;
  }

  isCrossSellingBatchLoaded(): boolean {
    return this.crossSellingBatchLoaded;
  }

  /**
   * Obtiene los productos y categorías de la cola de cross-selling
   * para hacer la llamada batch
   */
  getCrossSellingQueueData(): Array<{ productId: string; categories?: CategoryFilter[] }> {
    return this.crossSellingQueue.map(item => ({
      productId: item.productId,
      categories: item.categories
    }));
  }

  hasMoreRecommendations(): boolean {
    return this.currentUpsellingIndex < this.upsellingQueue.length ||
           this.currentCrossSellingIndex < this.crossSellingQueue.length;
  }

  isFlowActive(): boolean {
    return this.recommendationFlowStarted && !this.flowFinishedSubject.value;
  }

  private notifyServerErrorIfNeeded(error: any) {
    if (error?.status === 500) {
      this.errorMessageSubject.next(RECOMMENDATIONS_SERVER_ERROR_MESSAGE);
      this.isLoadingSubject.next(false);
    }
  }
}

